import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, orderBy, doc, getDoc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { MessageSquare, Plus, List, Edit, Trash2, Share2, QrCode, Eye } from 'lucide-react';
import FormEditor from '../components/form-connect/FormEditor';

const FormConnect = () => {
  const { currentUser } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, create, edit, preview
  const [currentForm, setCurrentForm] = useState(null);
  const [formTemplates, setFormTemplates] = useState([
    {
      id: 'contact',
      name: 'Contact Form',
      description: 'Basic contact information collection',
      fields: [
        { id: 'name', type: 'text', label: 'Full Name', placeholder: 'Enter your full name', required: true },
        { id: 'email', type: 'email', label: 'Email Address', placeholder: 'Enter your email', required: true },
        { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Enter your phone number', required: true },
        { id: 'message', type: 'textarea', label: 'Message', placeholder: 'How can we help you?', required: false }
      ]
    },
    {
      id: 'event',
      name: 'Event Registration',
      description: 'Collect event registration details',
      fields: [
        { id: 'name', type: 'text', label: 'Full Name', placeholder: 'Enter your full name', required: true },
        { id: 'email', type: 'email', label: 'Email Address', placeholder: 'Enter your email', required: true },
        { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Enter your phone number', required: true },
        { id: 'attendees', type: 'number', label: 'Number of Attendees', placeholder: '1', required: true },
        { id: 'dietary', type: 'select', label: 'Dietary Restrictions', 
          options: ['None', 'Vegetarian', 'Vegan', 'Gluten-Free', 'Other'],
          placeholder: 'Select an option', required: false }
      ]
    },
    {
      id: 'product',
      name: 'Product Inquiry',
      description: 'Collect product interest information',
      fields: [
        { id: 'name', type: 'text', label: 'Full Name', placeholder: 'Enter your full name', required: true },
        { id: 'email', type: 'email', label: 'Email Address', placeholder: 'Enter your email', required: true },
        { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Enter your phone number', required: true },
        { id: 'product', type: 'select', label: 'Product Interest', 
          options: ['Product A', 'Product B', 'Product C', 'Other'],
          placeholder: 'Select a product', required: true },
        { id: 'budget', type: 'select', label: 'Budget Range', 
          options: ['$0-$100', '$100-$500', '$500-$1000', '$1000+'],
          placeholder: 'Select your budget', required: false },
        { id: 'message', type: 'textarea', label: 'Additional Information', placeholder: 'Tell us more about your needs', required: false }
      ]
    }
  ]);

  // Fetch user's forms from Firestore
  useEffect(() => {
    const fetchForms = async () => {
      if (!currentUser?.uid) return;
      
      try {
        setLoading(true);
        const formsRef = collection(db, 'forms');
        const q = query(
          formsRef,
          where('userId', '==', currentUser.uid),
          orderBy('updatedAt', 'desc')
        );
        
        const formsSnapshot = await getDocs(q);
        const formsList = [];
        
        formsSnapshot.forEach((doc) => {
          formsList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setForms(formsList);
      } catch (error) {
        console.error('Error fetching forms:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchForms();
  }, [currentUser]);

  // Create a new form
  const createForm = async (formData) => {
    try {
      setLoading(true);
      
      const newForm = {
        ...formData,
        userId: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        submissions: 0
      };
      
      const docRef = await addDoc(collection(db, 'forms'), newForm);
      
      setForms([
        {
          id: docRef.id,
          ...newForm,
          createdAt: new Date(),
          updatedAt: new Date()
        },
        ...forms
      ]);
      
      setView('list');
      return docRef.id;
    } catch (error) {
      console.error('Error creating form:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing form
  const updateForm = async (formId, formData) => {
    try {
      setLoading(true);
      
      const formRef = doc(db, 'forms', formId);
      
      await updateDoc(formRef, {
        ...formData,
        updatedAt: serverTimestamp()
      });
      
      setForms(forms.map(form => 
        form.id === formId 
          ? { ...form, ...formData, updatedAt: new Date() } 
          : form
      ));
      
      setView('list');
    } catch (error) {
      console.error('Error updating form:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a form
  const deleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const formRef = doc(db, 'forms', formId);
      await deleteDoc(formRef);
      
      setForms(forms.filter(form => form.id !== formId));
    } catch (error) {
      console.error('Error deleting form:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    const template = formTemplates.find(t => t.id === templateId);
    if (template) {
      setCurrentForm({
        name: template.name,
        description: template.description,
        fields: [...template.fields],
        settings: {
          whatsappNumber: currentUser?.phoneNumber || '',
          messageTemplate: 'New form submission:\n\n{{formData}}',
          redirectUrl: '',
          theme: 'default'
        }
      });
      setView('edit');
    }
  };

  // Handle creating a blank form
  const handleCreateBlank = () => {
    setCurrentForm({
      name: 'Untitled Form',
      description: '',
      fields: [
        { id: 'name', type: 'text', label: 'Full Name', placeholder: 'Enter your full name', required: true },
        { id: 'email', type: 'email', label: 'Email Address', placeholder: 'Enter your email', required: true },
        { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Enter your phone number', required: true }
      ],
      settings: {
        whatsappNumber: currentUser?.phoneNumber || '',
        messageTemplate: 'New form submission:\n\n{{formData}}',
        redirectUrl: '',
        theme: 'default'
      }
    });
    setView('edit');
  };

  // Handle editing an existing form
  const handleEditForm = async (formId) => {
    try {
      setLoading(true);
      
      const formRef = doc(db, 'forms', formId);
      const formSnap = await getDoc(formRef);
      
      if (formSnap.exists()) {
        setCurrentForm({
          id: formId,
          ...formSnap.data()
        });
        setView('edit');
      }
    } catch (error) {
      console.error('Error fetching form details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate form URL
  const getFormUrl = (formId) => {
    return `${window.location.origin}/form/${formId}`;
  };

  // Copy form URL to clipboard
  const copyFormUrl = (formId) => {
    // Find the form in our list
    const form = forms.find(f => f.id === formId);
    
    // Check if the form has a WhatsApp number set
    if (form && !form.settings?.whatsappNumber) {
      alert('You need to add your WhatsApp number before sharing this form. Your WhatsApp number is important to receive form submissions directly to your phone.');
      
      // Redirect to edit this form to set WhatsApp number
      handleEditForm(formId);
      return;
    }
    
    const url = getFormUrl(formId);
    navigator.clipboard.writeText(url);
    alert('Form URL copied to clipboard!');
  };

  // Render form list view
  const renderFormList = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Your Forms</h2>
          <button
            onClick={() => setView('create')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200 flex items-center"
          >
            <Plus className="h-5 w-5 mr-1" />
            Create Form
          </button>
        </div>

        {forms.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <MessageSquare className="h-12 w-12 mx-auto text-indigo-600 mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No forms yet</h3>
            <p className="text-gray-600 mb-6">
              Create your first form to start collecting information and connecting with your audience via WhatsApp.
            </p>
            <button
              onClick={() => setView('create')}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200 inline-flex items-center"
            >
              <Plus className="h-5 w-5 mr-1" />
              Create Your First Form
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forms.map((form) => (
              <div key={form.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">{form.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{form.description}</p>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <span className="mr-4">
                      <span className="font-medium">{form.fields?.length || 0}</span> fields
                    </span>
                    <span>
                      <span className="font-medium">{form.submissions || 0}</span> submissions
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleEditForm(form.id)}
                      className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-sm text-gray-700 transition-colors duration-200"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    
                    <button
                    onClick={() => copyFormUrl(form.id)}
                    className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-sm text-gray-700 transition-colors duration-200 relative group"
                    >
                    <Share2 className="h-4 w-4 mr-1" />
                    Share
                      {!form.settings?.whatsappNumber && (
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-red-500 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                          WhatsApp number required
                        </span>
                      )}
                    </button>
                    
                    <button
                      onClick={() => window.open(`/form/${form.id}/preview`, '_blank')}
                      className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-sm text-gray-700 transition-colors duration-200"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </button>
                    
                    <button
                      onClick={() => deleteForm(form.id)}
                      className="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 rounded-md text-sm text-red-700 transition-colors duration-200"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-6 py-3 text-right">
                  <span className="text-xs text-gray-500">
                    Last updated: {form.updatedAt && typeof form.updatedAt.toDate === 'function' 
                      ? form.updatedAt.toDate().toLocaleDateString() 
                      : form.updatedAt instanceof Date 
                        ? form.updatedAt.toLocaleDateString()
                        : 'N/A'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render template selection view
  const renderTemplateSelection = () => {
    return (
      <div>
        <div className="flex items-center mb-6">
          <button
            onClick={() => setView('list')}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Create New Form</h2>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Start from scratch</h3>
          <p className="text-gray-600 mb-4">
            Create a blank form and customize it to your needs.
          </p>
          <button
            onClick={handleCreateBlank}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200"
          >
            Create Blank Form
          </button>
        </div>

        <h3 className="text-lg font-medium text-gray-900 mb-4">Or choose a template</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {formTemplates.map((template) => (
            <div key={template.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-1">{template.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{template.description}</p>
                <p className="text-sm text-gray-500 mb-4">
                  <span className="font-medium">{template.fields.length}</span> fields
                </p>
                <button
                  onClick={() => handleTemplateSelect(template.id)}
                  className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200"
                >
                  Use This Template
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Main render function
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <MessageSquare className="mr-3 h-8 w-8 text-indigo-600" />
          FormConnect
        </h1>
        <p className="text-gray-600 mt-2">
          Create custom forms and connect with your audience through WhatsApp.
        </p>
      </div>

      {view === 'list' && renderFormList()}
      {view === 'create' && renderTemplateSelection()}
      {view === 'edit' && (
        <FormEditor
          form={currentForm}
          onSave={(formData) => {
            if (formData.id) {
              updateForm(formData.id, formData);
            } else {
              createForm(formData);
            }
          }}
          onCancel={() => setView('list')}
        />
      )}
    </div>
  );
};

export default FormConnect;
